import { Check, X } from "lucide-react";
import { SectionLabel } from "@/components/yanre/SectionLabel";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";
import { useLocale } from "@/i18n/LocaleContext";
import { AFRICA_INVEST } from "@/i18n/markets";

const FITNESS = ["Recurring memberships","No merchandise inventory","No daily replenishment","Prepaid / recurring access","Lean operation potential","Digital management","Equipment = long-term productive asset"];
const RETAIL = ["Sell every day","Stock to manage","Constant purchasing","Transaction-by-transaction revenue","Inventory operation","More operational complexity","Inventory must keep turning"];

// Why invest — structural business-model comparison (objective, no disparagement)
export const WhyInvest = ({ index }) => {
  const { t } = useLocale();
  return (
    <section id="why-fitness" className="section-light relative overflow-hidden" data-testid="why-invest-section">
      <div className="grid-lines-light absolute inset-0 opacity-40" />
      <div className="container-edge relative py-16 lg:py-24">
        <Reveal>
          <SectionLabel index={index} tone="light">Why Invest in Fitness</SectionLabel>
        </Reveal>
        <h2 className="mt-8 max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tight text-carbon-900 sm:text-5xl lg:text-6xl">
          <RevealLines
            lines={[
              t("wi.h1a", "Looking for your next business?"),
              <span key="f" className="text-signal">{t("wi.h1b", "Look at fitness.")}</span>,
            ]}
          />
        </h2>
        <div className="mt-12 grid gap-px lg:grid-cols-2" data-testid="business-comparison">
          <Reveal>
            <div className="h-full bg-carbon-900 p-7 sm:p-9">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-signal">{t("wi.fitnessCol", "Fitness Business")}</p>
              <ul className="mt-7 space-y-3.5">
                {FITNESS.map((item, i) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-titanium sm:text-base">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-signal" />
                    {t(`wi.f${i + 1}`, item)}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full border border-carbon-900/10 bg-white p-7 sm:p-9">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-carbon-900/50">{t("wi.retailCol", "Traditional Retail")}</p>
              <ul className="mt-7 space-y-3.5">
                {RETAIL.map((item, i) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-carbon-900/60 sm:text-base">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-carbon-900/30" />
                    {t(`wi.r${i + 1}`, item)}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-carbon-900/40">
            {t("wi.note", "A structural business-model comparison — not a judgment of other industries.")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const MARKET_STATS = [
  { value: "€39.1B", key: "mp.s1", label: "European fitness market revenue in 2025" },
  { value: "75.5M", key: "mp.s2", label: "Fitness club members in Europe in 2025" },
  { value: "+8%", key: "mp.s3", label: "Average global fitness industry revenue growth in 2024" },
  { value: "23.6%", key: "mp.s4", label: "Median EBITDA margin among operators in the referenced HFA industry benchmarking study" },
];

const OPERATOR_STATS = [
  { value: "+2.8M", key: "mp.o1", label: "Members added by Europe's Top 20 operators in 2025" },
  { value: "€7.9B", key: "mp.o2", label: "Combined revenue of Europe's Top 20 operators in 2025" },
  { value: "27", key: "mp.o3", label: "Major European fitness M&A transactions in 2025" },
];

// AFRICA WAVE 1 variant — same visual structure, qualitative Africa-focused
// investment messaging instead of European market statistics. Rendered ONLY
// on the 8 Africa country-market routes; every other route is unchanged.
const AfricaMarketProof = ({ index, lang }) => {
  const copy = AFRICA_INVEST[lang] || AFRICA_INVEST.en;
  return (
    <section className="section-darker relative overflow-hidden border-t border-carbon-700" data-testid="market-proof-section">
      <div className="grid-lines absolute inset-0 opacity-40" />
      <div className="container-edge relative py-16 lg:py-24">
        <Reveal>
          <SectionLabel index={index}>{copy.label}</SectionLabel>
        </Reveal>
        <div className="mt-12 grid gap-px sm:grid-cols-2 lg:grid-cols-4" data-testid="market-stats">
          {copy.stats.map(([value, label], i) => (
            <Reveal key={value} delay={i * 0.06}>
              <div className="h-full border border-carbon-700 bg-carbon-900 p-6 sm:p-7">
                <p className="font-display text-2xl font-extrabold uppercase leading-tight text-white sm:text-3xl">{value}</p>
                <p className="mt-3 text-sm leading-relaxed text-titanium/60">{label}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.12}>
          <div className="mt-10 grid gap-px border border-carbon-700 bg-carbon-800 sm:grid-cols-[auto_1fr_1fr_1fr]" data-testid="operators-block">
            <p className="flex items-center px-7 py-5 font-mono text-[11px] uppercase tracking-[0.25em] text-signal">
              {copy.opsLabel}
            </p>
            {copy.ops.map(([value, label]) => (
              <div key={value} className="border-t border-carbon-700 p-6 sm:border-l sm:border-t-0 sm:p-7">
                <p className="font-display text-xl font-extrabold uppercase leading-tight text-white sm:text-2xl">{value}</p>
                <p className="mt-2 text-sm text-titanium/60">{label}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-titanium/40">
            {copy.note}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export const MarketProof = ({ index }) => {
  const { t, market, lang } = useLocale();
  // Africa Wave 1 country markets get qualitative Africa messaging — no
  // European statistics. All other locales keep the approved original.
  if (market) return <AfricaMarketProof index={index} lang={lang} />;
  return (
    <section className="section-darker relative overflow-hidden border-t border-carbon-700" data-testid="market-proof-section">
      <div className="grid-lines absolute inset-0 opacity-40" />
      <div className="container-edge relative py-16 lg:py-24">
        <Reveal>
          <SectionLabel index={index}>Fitness Market Proof</SectionLabel>
        </Reveal>
        <div className="mt-12 grid gap-px sm:grid-cols-2 lg:grid-cols-4" data-testid="market-stats">
          {MARKET_STATS.map((s, i) => (
            <Reveal key={s.value} delay={i * 0.06}>
              <div className="h-full border border-carbon-700 bg-carbon-900 p-6 sm:p-7">
                <p className="font-display text-4xl font-extrabold text-white sm:text-5xl">{s.value}</p>
                <p className="mt-3 text-sm leading-relaxed text-titanium/60">{t(s.key, s.label)}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.12}>
          <div className="mt-10 grid gap-px border border-carbon-700 bg-carbon-800 sm:grid-cols-[auto_1fr_1fr_1fr]" data-testid="operators-block">
            <p className="flex items-center px-7 py-5 font-mono text-[11px] uppercase tracking-[0.25em] text-signal">
              {t("mp.opsLabel", "Big operators are expanding")}
            </p>
            {OPERATOR_STATS.map((s) => (
              <div key={s.value} className="border-t border-carbon-700 p-6 sm:border-l sm:border-t-0 sm:p-7">
                <p className="font-display text-3xl font-extrabold text-white sm:text-4xl">{s.value}</p>
                <p className="mt-2 text-sm text-titanium/60">{t(s.key, s.label)}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-titanium/40">
            {t("mp.src", "Sources: Health & Fitness Association · EuropeActive / Deloitte. Industry benchmarking data — not a guarantee of individual returns.")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export const NewToFitness = ({ index }) => {
  const { t } = useLocale();
  return (
    <section className="section-light relative overflow-hidden" data-testid="new-to-fitness-section">
      <div className="container-edge relative py-14 lg:py-20">
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <div>
            <Reveal>
              <SectionLabel index={index} tone="light">New to Fitness? That's OK.</SectionLabel>
            </Reveal>
            <h2 className="mt-6 font-display text-3xl font-extrabold uppercase leading-[0.95] tracking-tight text-carbon-900 sm:text-4xl lg:text-5xl">
              <RevealLines
                lines={[
                  t("nf.h1a", "You don't need to come from"),
                  <span key="i">
                    {t("nf.h1b", "the fitness industry.")}
                  </span>,
                ]}
              />
            </h2>
          </div>
          <Reveal delay={0.15}>
            <div className="space-y-4 border-l-2 border-signal pl-7">
              <p className="text-base font-light leading-relaxed text-carbon-700 sm:text-lg">
                {t("nf.b1", "You bring the investment and the business vision.")}
              </p>
              <p className="text-base font-light leading-relaxed text-carbon-700 sm:text-lg">
                {t("nf.b2", "We help you structure the equipment side of the project.")}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
